/**
 * The mandala.
 *
 * Week view: a seven-petal lotus, one petal per day, each petal carrying three bands
 * (Morning · Midday · Evening) that fill as the day is kept. A full day is a coloured petal;
 * a full week is a complete bloom, and the centre lights as consistency builds.
 *
 * Day view (small): the same flower reduced — three concentric rings for the three windows,
 * so the visual language is identical whether you are looking at a week, a day or a calendar cell.
 *
 * Colours come from CSS variables so a member's chosen palette flows through every mandala
 * without threading props down the tree.
 */
const WINDOWS = ['morning', 'midday', 'evening'];
const FALLBACK = ['#fcd34d', '#5eead4', '#a78bfa'];

const tone = (index) => `var(--mandala-${index + 1}, ${FALLBACK[index]})`;

const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

/** One lotus petal as a path, pointing out from the centre along `angle`. */
const petal = (cx, cy, inner, outer, angle, spread) => {
  const [tipX, tipY] = polar(cx, cy, outer, angle);
  const [baseLX, baseLY] = polar(cx, cy, inner, angle - spread);
  const [baseRX, baseRY] = polar(cx, cy, inner, angle + spread);
  const [ctrlLX, ctrlLY] = polar(cx, cy, outer * 0.92, angle - spread * 0.85);
  const [ctrlRX, ctrlRY] = polar(cx, cy, outer * 0.92, angle + spread * 0.85);
  return `M ${baseLX} ${baseLY} Q ${ctrlLX} ${ctrlLY} ${tipX} ${tipY}`
       + ` Q ${ctrlRX} ${ctrlRY} ${baseRX} ${baseRY} Z`;
};

const ring = (cx, cy, r) =>
  `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r}`;

/** Small flower: three rings, one per window. Used on day placards and calendar cells. */
export const Mandala = ({ windows = {}, size = 180, petals = true, glow = false, label,
                         weekPercent }) => {
  const cx = size / 2;
  const cy = size / 2;
  const shown = weekPercent !== undefined ? weekPercent : Math.round(
    WINDOWS.reduce((sum, w) => sum + (windows[w]?.percent || 0), 0) / 3);
  const band = size * 0.088;
  const outer = cx - band * 0.6;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible"
         role="img" aria-label={label || `${shown}% complete`}>
      {petals && size > 70 && (
        <g opacity="0.16">
          {Array.from({ length: 14 }, (_, i) => (
            <path key={i} d={petal(cx, cy, outer * 0.36, outer * 1.02, (360 / 14) * i, 12)}
                  fill="none" stroke="#e2e8f0" strokeWidth="0.5" />
          ))}
        </g>
      )}

      {WINDOWS.map((window, index) => {
        const r = outer - band * (index + 0.5) * 1.08;
        const percent = Math.min(100, windows[window]?.percent || 0);
        const circumference = 2 * Math.PI * r;
        return (
          <g key={window}>
            <path d={ring(cx, cy, r)} fill="none" stroke="rgba(255,255,255,0.09)"
                  strokeWidth={band * 0.8} />
            {percent > 0 && (
              <path d={ring(cx, cy, r)} fill="none" stroke={tone(index)}
                    strokeWidth={band * 0.8} strokeLinecap="round"
                    strokeDasharray={`${(circumference * percent) / 100} ${circumference}`}
                    opacity={percent >= 100 ? 0.95 : 0.6}
                    style={{ transition: 'stroke-dasharray 600ms ease, opacity 300ms ease' }} />
            )}
          </g>
        );
      })}

      <circle cx={cx} cy={cy} r={outer * 0.3}
              fill={glow ? 'rgba(252,211,77,0.2)' : 'rgba(255,255,255,0.05)'}
              stroke={glow ? 'rgba(252,211,77,0.6)' : 'rgba(255,255,255,0.12)'} strokeWidth="1" />
      {size > 90 && (
        <text x={cx} y={cy + size * 0.05} textAnchor="middle" fontSize={size * 0.14}
              fill="rgba(255,255,255,0.85)" fontWeight="600">{shown}%</text>
      )}
    </svg>
  );
};

const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** The week: seven petals, one per day, each with three bands for the three windows. */
export const WeekMandala = ({ days = [], size = 200, percent = 0 }) => {
  const cx = size / 2;
  const cy = size / 2;
  const outer = cx * 0.97;
  const inner = cx * 0.3;
  const spread = 22;

  return (
    <div className="relative inline-flex items-center justify-center" data-testid="week-mandala">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
           aria-label={`Week ${percent}% complete`}>
        {/* the quiet outer bloom that makes it a mandala rather than a chart */}
        <g opacity="0.18">
          {Array.from({ length: 14 }, (_, i) => (
            <path key={i} d={petal(cx, cy, inner * 1.15, outer, (360 / 14) * i + (360 / 28), 11)}
                  fill="none" stroke="#e2e8f0" strokeWidth="0.6" />
          ))}
        </g>

        {Array.from({ length: 7 }, (_, index) => {
          const day = days[index] || {};
          const angle = (360 / 7) * index;
          const filled = (day.percent || 0) >= 100;
          return (
            <g key={index}>
              {/* the petal itself — its outline brightens when the day is complete */}
              <path d={petal(cx, cy, inner, outer * 0.99, angle, spread)}
                    fill={filled ? 'rgba(252,211,77,0.12)' : 'rgba(255,255,255,0.05)'}
                    stroke={filled ? 'rgba(252,211,77,0.65)' : 'rgba(255,255,255,0.22)'}
                    strokeWidth="1"
                    style={{ transition: 'fill 500ms ease, stroke 500ms ease' }} />

              {/* three bands inside the petal: morning outermost, evening nearest the centre */}
              {WINDOWS.map((window, band) => {
                const percentDone = Math.min(100, day.windows?.[window]?.percent || 0);
                if (!percentDone) return null;
                const step = (outer * 0.99 - inner) / 3;
                const from = inner + step * (2 - band);
                const to = from + step * 0.88;
                const grown = from + (to - from) * (percentDone / 100);
                return (
                  <path key={window}
                        d={petal(cx, cy, from, grown, angle, spread * 0.82)}
                        fill={tone(band)} opacity={percentDone >= 100 ? 0.9 : 0.6}
                        style={{ transition: 'all 600ms ease' }} />
                );
              })}

              {/* the day's initial, sitting on the petal tip */}
              {size > 130 && (() => {
                const [x, y] = polar(cx, cy, outer * 0.86, angle);
                return (
                  <text x={x} y={y + 3} textAnchor="middle" fontSize={size * 0.055}
                        fill={filled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.35)'}
                        fontWeight="600">{DAY_LETTERS[index]}</text>
                );
              })()}
            </g>
          );
        })}

        {/* the centre: it lights as the week fills */}
        <circle cx={cx} cy={cy} r={inner * 0.92}
                fill={percent >= 80 ? 'rgba(252,211,77,0.22)' : 'rgba(255,255,255,0.05)'}
                stroke={percent >= 80 ? 'rgba(252,211,77,0.65)' : 'rgba(255,255,255,0.14)'}
                strokeWidth="1" />
        {Array.from({ length: 8 }, (_, i) => (
          <path key={i} d={petal(cx, cy, inner * 0.3, inner * 0.86, (360 / 8) * i, 16)}
                fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth="0.6" />
        ))}
        <text x={cx} y={cy + size * 0.035} textAnchor="middle" fontSize={size * 0.105}
              fill="rgba(255,255,255,0.9)" fontWeight="600">{percent}%</text>
      </svg>
    </div>
  );
};

export default Mandala;
